import { useState } from 'react';
import type { ReactNode } from 'react';
import { RouterLink } from '@redux-first-router-link';
import Observer from '@researchgate/react-intersection-observer';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import { Badge, cn } from '@simplywallst/ui-core/next';

import type { Company } from './StockTable';
import { PAGE_SIZE } from '../../query/fetchPage';

import Tooltip from '@/components/Tooltip';

import { ROUTE_STOCKS_MARKET_INDUSTRY } from '@/constants/routes';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';

const Snowflake = loadable(() => import('./Snowflake'), {
  resolveComponent: (components) => components.Snowflake,
});
const GrowthTooltip = loadable(() => import('./ValuationRatio'), {
  resolveComponent: (components) => components.GrowthTooltip,
});
const ValuationRatio = loadable(() => import('./ValuationRatio'), {
  resolveComponent: (components) => components.ValuationRatio,
});
const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle'),
  { ssr: false }
);

interface SSRLinkProps {
  url: string;
  children?: ReactNode;
  type?: 'dimmed' | 'good' | 'bad' | false;
}
export const SSRLink = ({ url, children, type }: SSRLinkProps) => (
  <RouterLink
    className={cn(
      'flex h-full items-center overflow-hidden truncate whitespace-pre-wrap no-underline',
      // Requires important to override the default text color as there's a
      // default set in the global stylesheet
      type === 'dimmed' && 'text-solid opacity-30',
      type === 'good' && '!text-good',
      type === 'bad' && '!text-bad',
      !type && 'text-[inherit]'
    )}
    as={RUNTIME_ENV === 'server' ? 'span' : undefined}
    to={RUNTIME_ENV === 'server' ? undefined : url}
    tabIndex={-1}
  >
    {children}
  </RouterLink>
);

interface Props {
  company: Company;
  index: number;
  isSticky: boolean;
  delayedCallback: (id: string) => void;
  cancelCallback: () => void;
  muteEvent: (e: any) => void;
  industry: string | 'any';
  market: PrimaryCountryISOElseGlobal;
  shortTermReturn: number | null;
  shortTermReturnFormatted: string;
  hideIndustryCol?: boolean;
}

export const Row = ({
  company: {
    companyId,
    uniqueSymbol,
    snowflakePoints,
    canonicalUrl,
    tickerSymbol,
    shortDescription,
    shortName,
    sharePrice,
    sharePriceFormatted,
    return1YrAbs,
    return1YrAbsFormatted,
    marketCap,
    marketCapFormatted,
    analystsPriceTarget,
    analystsPriceTargetFormatted,
    bestValuationRatio,
    peFormatted,
    pbFormatted,
    psFormatted,
    earningsGrowth,
    salesGrowth,
    analystCount,
    earningsGrowthFormatted,
    salesGrowthFormatted,
    dividendYield,
    dividendYieldFormatted,
    industrySlug,
    industryLabel,
    isRecentSearchEntry,
  },
  shortTermReturn,
  shortTermReturnFormatted,
  muteEvent,
  index,
  isSticky,
  industry,
  market,
  hideIndustryCol,
  delayedCallback,
  cancelCallback,
}: Props) => {
  const { trackEvent } = useTracking();
  const [isVisible, setIsVisible] = useState(index <= PAGE_SIZE - 1); // First page is always displayed for SSR
  const tableColSpan = industry === 'any' && !hideIndustryCol ? 12 : 11;
  return (
    <Observer
      threshold={[0, 1]}
      onChange={({ isIntersecting }) => {
        if (!isVisible && isIntersecting) {
          setIsVisible(true);
        }
      }}
    >
      <tr
        data-cy-id="stocks-table-row"
        onMouseOver={() => delayedCallback(companyId)}
        onMouseOut={cancelCallback}
        onFocus={() => delayedCallback(companyId)}
        onBlur={cancelCallback}
        onClick={() =>
          trackEvent({
            action: 'click',
            subject: 'table-row',
            uniqueSymbol: uniqueSymbol,
            companyId: companyId,
            index,
          })
        }
        className="has-[[data-el=main-link]:focus-within]:bg-surface-1 lg:has-[[data-el=main-link]:focus-within]:outline-dashed lg:has-[[data-el=main-link]:focus-within]:outline-1 lg:has-[[data-el=main-link]:focus-within]:outline-brand-01"
      >
        {isVisible ? (
          <>
            <td>
              <SSRLink url={canonicalUrl}>
                <div className="my-[-3px] -mr-x1">
                  <Snowflake points={snowflakePoints} />
                </div>
              </SSRLink>
            </td>
            <td
              className={cn(
                'lg:w-x20',
                // Mobile styles
                isSticky ? 'max-lg:w-[95px]' : 'max-lg:w-[146px]',
                'max-lg:sticky max-lg:-left-px max-lg:z-1 max-lg:!bg-surface',
                'max-lg:focus-within:!bg-surface-1',
                isSticky &&
                  'max-lg:border-0 max-lg:border-r max-lg:border-solid max-lg:border-soft max-lg:shadow-[2px_4px_4px_theme(colors.p.grey.180)]'
              )}
            >
              <RouterLink
                className="flex h-full w-inherit flex-col items-start justify-center overflow-hidden truncate whitespace-pre-wrap text-[inherit] no-underline"
                to={canonicalUrl}
                data-el="main-link"
              >
                <div className="grid grid-cols-[auto_auto] items-center gap-x0_5">
                  <div className="text-xs font-medium text-brand-01">
                    {tickerSymbol}
                  </div>
                  {isRecentSearchEntry && (
                    <div className="inline-grid justify-start">
                      <Badge tone="attention" size="small" className="-mt-0.5">
                        New
                      </Badge>
                    </div>
                  )}
                </div>
                <Tooltip
                  title={
                    <span className="inline-block max-w-[200px] text-pretty">
                      {shortDescription}
                    </span>
                  }
                  key={`table-tooltip-description${companyId}`}
                  light
                  zIndex={99999}
                >
                  <span
                    className="inline-block !w-auto !max-w-full cursor-help select-none truncate whitespace-nowrap border-0 border-b border-dotted border-strong !pr-0 text-tiny font-normal text-solid opacity-50"
                    onTouchEnd={muteEvent}
                    onClick={muteEvent}
                  >
                    {shortName}
                  </span>
                </Tooltip>
              </RouterLink>
            </td>
            <td>
              <SSRLink
                url={canonicalUrl}
                type={sharePrice === null && 'dimmed'}
              >
                {sharePriceFormatted}
              </SSRLink>
            </td>
            <td data-cy-id="short-term-return-value">
              <SSRLink
                url={canonicalUrl}
                type={
                  shortTermReturn === 0 ||
                  shortTermReturn === null ||
                  shortTermReturn === undefined
                    ? 'dimmed'
                    : shortTermReturn > 0
                      ? 'good'
                      : 'bad'
                }
              >
                {shortTermReturnFormatted}
              </SSRLink>
            </td>
            <td>
              <SSRLink
                url={canonicalUrl}
                type={
                  return1YrAbs === 0 ||
                  return1YrAbs === null ||
                  return1YrAbs === undefined
                    ? 'dimmed'
                    : return1YrAbs > 0
                      ? 'good'
                      : 'bad'
                }
              >
                {return1YrAbsFormatted}
              </SSRLink>
            </td>
            <td>
              <SSRLink url={canonicalUrl} type={marketCap === null && 'dimmed'}>
                {marketCapFormatted}
              </SSRLink>
            </td>
            <td>
              <SSRLink
                url={canonicalUrl}
                type={analystsPriceTarget === null && 'dimmed'}
              >
                {analystsPriceTargetFormatted}
              </SSRLink>
            </td>
            <td>
              <SSRLink
                url={canonicalUrl}
                type={bestValuationRatio.valuation === null && 'dimmed'}
              >
                {!!bestValuationRatio.valuation && (
                  <Tooltip
                    key={`table-tooltip-${companyId}`}
                    title={
                      <ValuationRatio
                        priceToEarnings={peFormatted}
                        priceToBook={pbFormatted}
                        priceToSales={psFormatted}
                      />
                    }
                    light
                    zIndex={99999}
                  >
                    <span
                      className="mr-1 inline-block cursor-help select-none whitespace-nowrap border-0 border-b border-dotted border-strong text-tiny text-[inherit]"
                      onTouchEnd={muteEvent}
                      onClick={muteEvent}
                    >
                      {bestValuationRatio.valuationType}
                    </span>
                  </Tooltip>
                )}
                {bestValuationRatio.valuationFormatted}
              </SSRLink>
            </td>
            <td>
              <SSRLink
                url={canonicalUrl}
                type={
                  (earningsGrowth || salesGrowth) === null
                    ? 'dimmed'
                    : undefined
                }
              >
                {(earningsGrowth || salesGrowth) && (
                  <Tooltip
                    key={`table-tooltip-${companyId}`}
                    title={
                      <GrowthTooltip
                        analystCount={analystCount}
                        earningsGrowth={earningsGrowthFormatted}
                        salesGrowth={salesGrowthFormatted}
                      />
                    }
                    light
                    zIndex={99999}
                  >
                    <span
                      className="mr-1 inline-block cursor-help select-none whitespace-nowrap border-0 border-b border-dotted border-strong text-tiny text-[inherit]"
                      onTouchEnd={muteEvent}
                      onClick={muteEvent}
                    >
                      {earningsGrowth ? 'E' : 'S'}
                    </span>
                  </Tooltip>
                )}
                {earningsGrowth
                  ? earningsGrowthFormatted
                  : salesGrowthFormatted}
              </SSRLink>
            </td>
            <td>
              <SSRLink
                url={canonicalUrl}
                type={dividendYield === null && 'dimmed'}
              >
                {dividendYieldFormatted}
              </SSRLink>
            </td>
            {industry === 'any' && !hideIndustryCol && (
              <td>
                <RouterLink
                  to={{
                    type: ROUTE_STOCKS_MARKET_INDUSTRY,
                    payload: {
                      market: market,
                      industry: industrySlug,
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // prevent doubling up on the tracking event on the <tr/>
                    trackEvent({
                      action: 'click',
                      subject: 'table-row-industry-column',
                      uniqueSymbol: uniqueSymbol,
                      companyId: companyId,
                      index,
                    });
                  }}
                  className="group/row flex h-full w-[140px] items-center truncate whitespace-pre-wrap text-[inherit] no-underline"
                >
                  <span className="truncate border-0 border-b border-solid border-soft pr-0 text-solid opacity-50 group-hover/row:text-brand-01 group-focus/row:outline-dashed group-focus/row:outline-1 group-focus/row:outline-brand-01">
                    {industryLabel}
                  </span>
                </RouterLink>
              </td>
            )}
            <td className="w-[50px] !pr-x1_25">
              <WatchlistAddRemoveCompanyToggle
                companyId={companyId}
                tickerSymbol={tickerSymbol}
                styleType="gentle"
                minimal
              />
            </td>
          </>
        ) : (
          <td colSpan={tableColSpan} />
        )}
      </tr>
    </Observer>
  );
};
